










































































import { PropType } from 'vue';
import { mapActions, mapGetters } from 'vuex';

import {
  IFetchQuoteProductDocumentsParams,
  IQuoteProductDocumentPaginatedListData
} from '@/services/document';
import mixins from '@/utils/mixins';
import Functions from '@/mixins/functions';
import {
  IQuoteDocument,
  IQuotePackageDocumentData,
  IQuoteProductDocumentData,
  QuoteDocument
} from '@/models';
import { ErrorManager } from '@/models/error';

import QuoteDocumentPackageFileUploadDialog from '@/components/quotes/QuoteDocumentPackageFileUploadDialog.vue';

const baseMixins = mixins(Functions);

interface Options extends InstanceType<typeof baseMixins> {
  $refs: {
    quoteDocumentPackageFileUploadDialog: InstanceType<
      typeof QuoteDocumentPackageFileUploadDialog
    >;
  };
}

export default baseMixins
  .extend<Options>({ functional: false })
  .extend({
    name: 'DocumentSelectionPicker',

    model: {
      prop: 'value',
      event: 'input'
    },

    props: {
      value: [Number, Object] as PropType<IQuoteDocument>,

      packageDocument: [Object] as PropType<IQuotePackageDocumentData>,

      externalErrorMessage: {
        type: Array as PropType<string[]>,
        default: () => []
      }
    },

    components: { QuoteDocumentPackageFileUploadDialog },

    data() {
      return {
        lazyValue: this.value,
        localQuoteDocumentList: [] as IQuoteDocument[],
        search: '',
        errorMessage: '',
        detailErrorMessage: '',
        loading: false,
        moreQuoteDocumentsLoading: false,
        apiPaginatedData: {} as IQuoteProductDocumentPaginatedListData,
        params: { page: 1, search: '' } as IFetchQuoteProductDocumentsParams
      };
    },

    computed: {
      ...mapGetters({
        getQuoteProductDocuments: 'document/getQuoteProductDocuments'
      }),

      internalValue: {
        get(): any {
          return this.lazyValue;
        },
        set(val: any) {
          this.lazyValue = val;
          this.$emit('input', val);
        }
      },

      customAttrs(): object {
        return this.$attrs;
      },

      errorMessageList(): string[] {
        let errorList: string[] = [];
        if (this.externalErrorMessage) {
          errorList = this.externalErrorMessage;
        }
        if (this.errorMessage) {
          errorList.push(this.errorMessage);
        }

        return errorList;
      }
    },

    watch: {
      search: {
        handler(val) {
          this.debounceFetchQuoteProductDocuments(
            typeof val == 'object' ? val.toString() : val
          );
        },
        immediate: true
      }
    },

    mounted() {
      this.localQuoteDocumentList = this.setLocalQuoteDocumentList(
        this.getQuoteProductDocuments
      );
    },

    methods: {
      ...mapActions({
        fetchQuoteProductDocuments: 'document/fetchQuoteProductDocuments'
      }),

      openUploadDocumentDialog() {
        this.$refs.quoteDocumentPackageFileUploadDialog.open(
          this.packageDocument
        );
      },

      setUploadedDocument(document: IQuotePackageDocumentData) {
        this.$emit('set-uploaded-document', document);
      },

      clearAllErrors() {
        this.errorMessage = '';
        this.detailErrorMessage = '';
      },

      debounceFetchQuoteProductDocuments(search?: string) {
        this.clearAllErrors();

        //to prevent duplicate search if the selected item is not found in the list
        //we add it locally
        if (typeof search == 'object') {
          if (this.internalValue) {
            const foundDocument = this.localQuoteDocumentList.find(
              (quoteDocument: IQuoteDocument) => {
                const documentId =
                  typeof this.internalValue == 'object'
                    ? this.internalValue.id
                    : this.internalValue;
                return quoteDocument.id == documentId;
              }
            );

            if (!foundDocument) {
              this.localQuoteDocumentList.push(this.internalValue);
            }
          }
          return;
        }

        this.loading = true;
        this.params = {
          page: 1,
          search: search
        };

        this.debounce(this.searchQuoteProductDocuments, 1000)(this.params);
      },

      async searchQuoteProductDocuments() {
        this.apiPaginatedData = (await this.retrievePaginatedQuoteProductDocuments(
          this.params
        )) as IQuoteProductDocumentPaginatedListData;

        this.localQuoteDocumentList = this.setLocalQuoteDocumentList(
          this.apiPaginatedData.results
        );
      },

      retrievePaginatedQuoteProductDocuments(
        params: IFetchQuoteProductDocumentsParams
      ) {
        this.loading = true;
        return this.$services.document
          .fetchQuoteProductDocuments(params)
          .then(data => {
            return data;
          })
          .catch(error => {
            this.errorMessage = ErrorManager.extractApiError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      },

      setLocalQuoteDocumentList(
        quoteProductDocuments: IQuoteProductDocumentData[]
      ): IQuoteDocument[] {
        const quoteDocuments = quoteProductDocuments.map(
          productDocument => new QuoteDocument(productDocument.quote_document!)
        );

        if (this.packageDocument.id) {
          const exist = quoteProductDocuments.find(
            productDocument => productDocument.id == this.packageDocument.id
          );

          if (!exist) {
            quoteDocuments.push(
              new QuoteDocument(
                this.packageDocument.quote_document as IQuoteDocument
              )
            );
          }
        }

        return quoteDocuments;
      },

      /**
       * when scroll bar reached the end of the menu we will call this function to get more data
       *
       * @param _entries
       * @param _observer
       * @param isIntersecting
       */
      async endOfIntersect(
        _entries: IntersectionObserverEntry[],
        _observer: IntersectionObserver,
        isIntersecting: boolean
      ) {
        //if intersection is reached and there's more date to be loaded then we fetch the data and
        //add it to our local list
        if (isIntersecting && this.apiPaginatedData.next) {
          this.params.page = this.params.page! + 1;
          this.moreQuoteDocumentsLoading = true;
          this.apiPaginatedData = (await this.retrievePaginatedQuoteProductDocuments(
            this.params
          )) as IQuoteProductDocumentPaginatedListData;

          const paginatedQuoteDocumentList = this.setLocalQuoteDocumentList(
            this.apiPaginatedData.results
          );

          this.localQuoteDocumentList = [
            ...this.localQuoteDocumentList,
            ...paginatedQuoteDocumentList
          ];

          this.moreQuoteDocumentsLoading = false;
        }
      },

      /**
       * removing filter from the quote search as to get the raw data from the server as is
       */
      itemSearch(item: any, queryText: string, itemText: string) {
        return true;
      }
    }
  });
